import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  useApparatusDevices,
  useApparatusReadings,
  useUnclaimedThings,
  type ApparatusDeviceRead,
} from "@/api/queries/apparatus";
import { useCurrentUser } from "@/api/queries/me";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";
import { formatDateTime, formatInteger } from "@/lib/format";
import { useAppStore } from "@/store/app-store";
import {
  ADVISORY_BANNER_BODY,
  ADVISORY_BANNER_TITLE,
  COMPENSATION_NOTE,
  formatHeartbeatAge,
  formatMetricValue,
  HEARTBEAT_WINDOW_SECONDS,
  isCompensationMetric,
  isUnreliableMetric,
  metricLabel,
  O2_UNRELIABLE_NOTE,
  reasonLabel,
} from "@/lib/apparatus";

/**
 * CBS-1 in-truck sensor status (RES-127). Read-only, respai_admin-only, and
 * deliberately text — the sensor is fixed in the cab, so there is no position to
 * map and no per-firefighter attribution to make.
 *
 * Three things this page must never do, all of them for the same reason (the
 * hardware cannot support the claim):
 *   - say "online" — no MQTT Last-Will, so absence is inferred, never proven
 *   - show battery/power/health — BATTERY_MONITOR_PRESENT=0 on HW v1.0
 *   - render a value when valid=false — the reason goes in its place
 */
export function ApparatusPage() {
  const { identity } = useAuth();
  if (!isRespaiAdmin(identity?.role)) {
    return (
      <p className="text-sm text-destructive">
        Apparatus status is restricted to RespAI platform administrators.
      </p>
    );
  }
  return <ApparatusPageInner />;
}

function ApparatusPageInner() {
  const me = useCurrentUser();
  const tz = me.data?.department?.timezone;
  const selectedDeptId = useAppStore((s) => s.selectedDepartmentId);
  const showDepartmentColumn = selectedDeptId === "all";
  const { data: devices, isLoading, isError } = useApparatusDevices();

  const [selectedApparatusId, setSelectedApparatusId] = useState<string | null>(
    null,
  );

  // Default the readings panel to the first device that is actually bound to an
  // apparatus, so a single-truck pilot lands on its data without a click.
  const effectiveApparatusId =
    selectedApparatusId ??
    devices?.find((d) => d.apparatus_id)?.apparatus_id ??
    null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Apparatus</h1>
        <p className="text-sm text-muted-foreground">
          CBS-1 cab sensors and their most recent readings.
        </p>
      </header>

      <AdvisoryBanner />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {devices?.length ?? 0} sensor{(devices?.length ?? 0) !== 1 && "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-6" />
              <Skeleton className="h-6" />
            </div>
          ) : isError ? (
            <p className="p-6 text-sm text-destructive">
              Failed to load apparatus sensors. Check your connection and try
              again.
            </p>
          ) : devices && devices.length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Apparatus</Th>
                  <Th>IoT thing</Th>
                  {showDepartmentColumn && <Th>Department</Th>}
                  <Th>Last heard from</Th>
                  <Th>Claimed</Th>
                  <Th>Readings</Th>
                </Tr>
              </Thead>
              <Tbody>
                {devices.map((d) => (
                  <DeviceRow
                    key={d.id}
                    device={d}
                    tz={tz}
                    showDepartmentColumn={showDepartmentColumn}
                    isSelected={
                      Boolean(d.apparatus_id) &&
                      d.apparatus_id === effectiveApparatusId
                    }
                    onSelect={() =>
                      setSelectedApparatusId(d.apparatus_id ?? null)
                    }
                  />
                ))}
              </Tbody>
            </Table>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              No CBS-1 sensors claimed yet.
            </p>
          )}
        </CardContent>
      </Card>

      {effectiveApparatusId && (
        <ReadingsCard apparatusId={effectiveApparatusId} tz={tz} />
      )}

      <UnclaimedCard tz={tz} />
    </div>
  );
}

/**
 * Things publishing to our IoT endpoint that nobody has claimed (RES-144).
 *
 * Answers the question the card above cannot: a device that was never claimed
 * has no row in the sensor list at all, so "publishing but unclaimed" and "dead"
 * look identical up there. This section is the only place they separate.
 *
 * No department column — there is no department. That is what unclaimed means.
 */
function UnclaimedCard({ tz }: { tz?: string }) {
  const { data, isLoading, isError } = useUnclaimedThings();
  const count = data?.length ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Unclaimed things</CardTitle>
        <p className="text-sm text-muted-foreground">
          Seen publishing to our IoT endpoint, but not claimed by any department.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="space-y-2 p-6">
            <Skeleton className="h-6" />
            <Skeleton className="h-6" />
          </div>
        ) : isError ? (
          <p className="p-6 text-sm text-destructive">
            Failed to load unclaimed things. Check your connection and try again.
          </p>
        ) : count > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <Th>IoT thing</Th>
                <Th>First seen</Th>
                <Th>Last seen</Th>
                <Th>Messages dropped</Th>
              </Tr>
            </Thead>
            <Tbody>
              {/* Server-sorted newest-last-seen first; not re-sorted here, so
                  one definition of "newest" exists rather than two. */}
              {data?.map((thing) => (
                <Tr key={thing.iot_thing_name}>
                  <Td className="font-mono text-xs">{thing.iot_thing_name}</Td>
                  <Td className="text-sm">
                    {formatDateTime(thing.first_seen_at, tz)}
                  </Td>
                  {/* Relative, because "is it on the wire right now" is the
                      question. `formatHeartbeatAge` is the shared relative-age
                      formatter — an unclaimed thing has no heartbeat of its own
                      (the poller drops its messages before any device row
                      exists), this is the last dropped message. Its contract is
                      what matters here: it never returns a presence word. */}
                  <Td className="text-sm">
                    {formatHeartbeatAge(thing.last_seen_at)}
                  </Td>
                  {/* Dropped messages, not distinct metrics. A single CBS-1
                      contributes ~37/min, so a large number climbing fast is
                      itself the signal that something real is on the wire. */}
                  <Td className="text-sm tabular-nums">
                    {formatInteger(thing.message_count)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          /* The common case, and good news. Muted like the sensor list's empty
             state and never destructive: an empty section here means every
             device on the wire is accounted for. */
          <p className="p-6 text-sm text-muted-foreground">
            No unclaimed things — every device seen on the wire has been claimed.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AdvisoryBanner() {
  return (
    <div
      role="note"
      className="flex gap-3 rounded-md border border-warning bg-warning/10 p-4"
    >
      <AlertTriangle
        className="mt-0.5 h-5 w-5 shrink-0 text-warning"
        aria-hidden="true"
      />
      <div className="space-y-1">
        <p className="text-sm font-semibold">{ADVISORY_BANNER_TITLE}</p>
        <p className="text-sm text-muted-foreground">{ADVISORY_BANNER_BODY}</p>
      </div>
    </div>
  );
}

function DeviceRow({
  device,
  tz,
  showDepartmentColumn,
  isSelected,
  onSelect,
}: {
  device: ApparatusDeviceRead;
  tz?: string;
  showDepartmentColumn: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <Tr data-state={isSelected ? "selected" : undefined}>
      <Td>
        {device.apparatus_name ?? (
          <span className="text-muted-foreground">Not linked to apparatus</span>
        )}
      </Td>
      <Td className="font-mono text-xs">{device.iot_thing_name}</Td>
      {showDepartmentColumn && (
        <Td className="font-mono text-xs">{device.department_id.slice(0, 8)}</Td>
      )}
      <Td className="text-sm">
        <div className="flex items-center gap-2">
          <span>{formatHeartbeatAge(device.last_heard_from)}</span>
          {device.heartbeat_stale && (
            <Badge
              variant="warning"
              title={`No heartbeat in the last ${HEARTBEAT_WINDOW_SECONDS}s`}
            >
              Stale
            </Badge>
          )}
        </div>
      </Td>
      <Td className="text-sm">{formatDateTime(device.claimed_at, tz)}</Td>
      <Td>
        {device.apparatus_id ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onSelect}
            disabled={isSelected}
          >
            {isSelected ? "Showing" : "Show"}
          </Button>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </Td>
    </Tr>
  );
}

function ReadingsCard({
  apparatusId,
  tz,
}: {
  apparatusId: string;
  tz?: string;
}) {
  const { data, isLoading, isError } = useApparatusReadings(apparatusId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Latest readings</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="space-y-2 p-6">
            <Skeleton className="h-6" />
            <Skeleton className="h-6" />
          </div>
        ) : isError ? (
          <p className="p-6 text-sm text-destructive">
            Failed to load readings. Check your connection and try again.
          </p>
        ) : data ? (
          <>
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-3 text-sm">
              <span className="font-mono text-xs">{data.iot_thing_name}</span>
              <span className="text-muted-foreground">
                heartbeat {formatHeartbeatAge(data.last_heard_from)}
              </span>
              {data.heartbeat_stale && <Badge variant="warning">Stale</Badge>}
              {!data.calibrated && <Badge variant="warning">Uncalibrated</Badge>}
            </div>
            <Table>
              <Thead>
                <Tr>
                  <Th>Metric</Th>
                  <Th>Value</Th>
                  <Th>Measured</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.metrics.map((m) => (
                  <Tr key={m.metric}>
                    <Td>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                          {metricLabel(m.metric)}
                        </span>
                        {isUnreliableMetric(m.metric) && (
                          <Badge variant="destructive" title={O2_UNRELIABLE_NOTE}>
                            Unreliable
                          </Badge>
                        )}
                      </div>
                      {isUnreliableMetric(m.metric) && (
                        <p className="mt-1 max-w-prose text-xs text-muted-foreground">
                          {O2_UNRELIABLE_NOTE}
                        </p>
                      )}
                      {/* Not a warning — the reading is trustworthy, it just
                          measures the board rather than the cab. A destructive
                          badge here would overstate it. */}
                      {isCompensationMetric(m.metric) && (
                        <p className="mt-1 max-w-prose text-xs text-muted-foreground">
                          {COMPENSATION_NOTE}
                        </p>
                      )}
                    </Td>
                    <Td>
                      <MetricValue metric={m} />
                    </Td>
                    <Td className="text-sm text-muted-foreground">
                      <MetricTimestamp metric={m} tz={tz} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function MetricTimestamp({
  metric,
  tz,
}: {
  metric: { time_valid: boolean; measured_at?: string | null; received_at?: string | null };
  tz?: string;
}) {
  // No arrival time at all means no reading has ever come in for this metric —
  // the row exists only so the page shape stays stable from an empty table
  // onward. Saying "— (received)" there would claim a receipt happened at an
  // unknown time, which is the opposite of what null means.
  if (!metric.received_at) {
    return <span>Never</span>;
  }
  // `measured_at` is only populated when the device clock is trustworthy;
  // otherwise the server's receipt time is the only honest timestamp.
  if (metric.time_valid && metric.measured_at) {
    return <span>{formatDateTime(metric.measured_at, tz)}</span>;
  }
  return <span>{formatDateTime(metric.received_at, tz)} (received)</span>;
}

function MetricValue({
  metric,
}: {
  metric: { valid: boolean; value?: string | null; unit?: string | null; reason?: string | null };
}) {
  // Two independent gates, both required. `valid` is the contract; a null/
  // unparseable value is the serializer failing (decimals arrive as strings).
  // Either way there is no number to show, so the reason takes its place.
  const rendered = metric.valid
    ? formatMetricValue(metric.value, metric.unit)
    : null;

  if (rendered === null) {
    return (
      <span className="text-sm text-muted-foreground">
        {reasonLabel(metric.reason)}
      </span>
    );
  }
  return <span className="tabular-nums">{rendered}</span>;
}
